import React, {useEffect, useState} from 'react';
import {ToxnotComponentProperties} from "../BaseApplicationComponents/ToxnotComponent";
import * as SurveyResponseController from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";
import {SurveyResponseState} from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseStates";
import {ToxnotSignInRequiredComponent} from "../BaseApplicationComponents/ToxnotSignInRequiredComponent";
import {TeamRole} from "../../../store/Models/UserContext";
import {connect} from "react-redux";
import {ApplicationState} from "../../../store";
import {ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter} from "../ToxnotReactModal";
import TextInput from "../TextInput";
import {ToxnotServiceClient} from "../../../store/ToxnotServiceClient";
import SaveDataManager from "../../../utilities/SaveDataManager";
import {GroupedResponseInventories} from "../../../store/Models/Surveys/SurveyDataModel"; // Create a CSS file for styling

import './styles.css';
import { CoreSurveyResponseData } from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import { SimpleInventory } from '../../../store/Models/Inventories/SimpleInventory';
import { LoadingSpinner } from '../LoadingSpinner';

type Props =
    ToxnotComponentProperties &
    typeof SurveyResponseController.actionCreators &
    SurveyResponseState;

class AddMaterials extends ToxnotSignInRequiredComponent<Props> {
    public constructor(props: Props) {
        super(props);
    }

    protected getDisallowedRoles(): TeamRole[] {
        return [TeamRole.Marketing, TeamRole.Viewer];
    }

    protected renderConcreteComponent(): JSX.Element | null {
        return (
            <MaterialListComponent parentProps={this.props}/>
        )
    }
}

export default connect(
    (state: ApplicationState) => ({...state.surveyResponseState, appServices: state.appServices}),
    SurveyResponseController.actionCreators
)(AddMaterials as any);

interface MaterialListProps {
    parentProps: Props;
}

const MaterialListComponent: React.FC<MaterialListProps> = ({parentProps}) => {
    const [parent] = useState(parentProps);
    const serviceClient = new ToxnotServiceClient();
    const [allCoreData, setAllCoreData] = useState<CoreSurveyResponseData | null>(null);
    const [materials, setMaterials] = useState<SimpleInventory[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            const serviceClient = new ToxnotServiceClient();

            try {
                const result = await serviceClient.getCoreSurveyResponseDataForAddedMaterials(parent.responseId);

                setAllCoreData(result.data);
                // Get inventory list after core data is loaded
                const inventoryList = SurveyResponseController.getMainSupplierFacingMaterialInventoryList(result.data).orderBy(inv => inv.name);
                setMaterials(inventoryList);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
        
    }, [parent.responseId]);
    
    const [materialName, setMaterialName] = useState("");
    
    const deleteInventory = async (inventoryId: string) =>
        (await serviceClient.deleteInventory(parent.responseId, inventoryId)).success;
    
    const deleteGroup = async (groups: any) => {
        const response = await serviceClient.dataCollectionResponseWriteGroupedResponseInventories(
            parent.responseId,
            groups
        )

        if (response.success && response.data) {
            parent.setGroupsState(response.data!);
            await setMaterials(SurveyResponseController.getMainSupplierFacingMaterialInventoryList());
        }

        return response.success;
    };

    const inventorySaveDataManager: SaveDataManager<string> = new SaveDataManager(deleteInventory, parent.appServices);
    inventorySaveDataManager.enable();

    const groupSaveDataManager: SaveDataManager<GroupedResponseInventories> = new SaveDataManager(deleteGroup, parent.appServices);
    groupSaveDataManager.enable();

    const handleRemoveMaterial = async (id: string) => {
        const groupList = Object.values(parent.coreData?.response?.groupedResponseInventories ?? []);
        const group = groupList.find(x => x.groupInventoryId === id);
        let currentPage = parent.navContext.currentPageOfMaterialsIndex;
        let isGroup = false;
        
        if (group) {
            const response = parent.coreData!.response;
            isGroup = true;
            const newGroups = {...response.groupedResponseInventories};
            let newMaterialIdsDataToLoadList = [...parent.coreData!.materialIdsDataToLoadList];

            delete newGroups[group.groupName];

            const newInventoryInfoTable = {...parent.coreData!.inventoryInfoTable};

            if (group.groupInventoryId !== null) {
                delete newInventoryInfoTable[group.groupInventoryId];
                newMaterialIdsDataToLoadList = newMaterialIdsDataToLoadList.filter(i => i != group.groupInventoryId);
                group.responderOwnedResponseInventoryIdList.forEach(initialInventoryId => {
                    if (!newMaterialIdsDataToLoadList.some(i => i == initialInventoryId))
                        newMaterialIdsDataToLoadList.push(initialInventoryId);
                });
            }

            parent.setSurveyResponseState({
                coreData: {
                    ...parent.coreData!,
                    inventoryInfoTable: newInventoryInfoTable,
                    materialIdsDataToLoadList: newMaterialIdsDataToLoadList,
                }
            });

            await groupSaveDataManager.saveAsync(newGroups);
            //await parent.getInitialSurveyData(response.responseId);
        } else {
            await inventorySaveDataManager.saveAsync(id);
        }

        setMaterials([...materials.filter((material) => id !== material.inventoryId)]);

        parent.deleteSurveyInventory(id, isGroup);

        if(isGroup){
            parent.getPaginatedSurveyData(currentPage);
        }
    };

    const handleAddMaterialButtonClick = () =>
        $('#add-material-modal').modal('show');

    const handleAddMaterial = async () => {
        const response = await serviceClient.addInventory(parent.responseId, materialName);
        let newId = response.data ? Object.values(response.data).join('') : null;

        if (response.success && newId) {
            const inventory = {
                inventoryId: newId,
                name: materialName === "" ? parent.appServices.localizeString("Unnamed Material") : materialName,
                isComponent: false,
                plantName: "",
                description: "",
                productNumber: "",
                supplierPartNumber: "",
                ownerId: "",
                supplierId: null,
                supplierName: null,
                inventoryValidationStatus: "1"
            };
            materials.push(inventory);
            setMaterials([...materials]);

            await parent.addSurveyInventory(inventory);
            await parent.updateValidationResult([newId]);
        }

        setMaterialName("");
    };

    return (
        <>
            <div className="m-b-10 page-title-border">
                <h4 className="m-t-10 m-b-10">{parent.appServices.localizeString("Add Materials")}</h4>
            </div>
            <div className="material-list-container col-xs-12 col-lg-6 p-0">

                <ToxnotReactModal modalId={"add-material-modal"}
                                  localizedTitle={parentProps.appServices.localizeString("Add Material")}>
                    <ToxnotReactModalBody>
                        <div className="form-group">
                            <label>{parent.appServices.localizeString("Material Name")}</label>
                            <TextInput onValueChange={e => setMaterialName(e)} value={materialName}/>
                        </div>
                    </ToxnotReactModalBody>
                    <ToxnotReactModalFooter localizer={parentProps.appServices}>
                        <button type="button" className="btn btn-toxnot-primary" data-dismiss="modal"
                                onClick={() => handleAddMaterial()}>{parentProps.appServices.localizeString("Create Material")}
                        </button>
                    </ToxnotReactModalFooter>
                </ToxnotReactModal>

                <button id="add-materials" className="btn btn-default" name="add-materials" onClick={() => handleAddMaterialButtonClick()}>
                    <span className="glyphicon glyphicon-plus"/>{parentProps.appServices.localizeString("Materials")}
                </button>
                <div className="material-list-component">
                    {isLoading ? (
                        <div className="flex items-center justify-center p-8">
                            <LoadingSpinner />
                        </div>
                    ) : (
                        materials.map((material) => (
                            <div key={material.inventoryId} className="material-item">
                                <span>{material.name}</span>
                                <button
                                    className="remove-material-button"
                                    onClick={() => handleRemoveMaterial(material.inventoryId)}
                                >
                                    <span className="glyphicon glyphicon-remove" />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </>
    );
};
